import { Card } from "@app/shared/components/Card";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import { type FC } from "react";
import { type QuestionnaireTemplate } from "../../types";
import { CreateQuestionnaireModal } from "../CreateQuestionnaireModal";
import { QuickAnswerModal } from "../QuickAnswerModal";
import { CreateQuestionnaireFromUploadModal } from "./CreateQuestionnaireFromUploadModal";
import { useQuestionnairesDashboardTranslations } from "./i18n";

export const QuestionnairesDashboard: FC = () => {
    const { t, keys } = useQuestionnairesDashboardTranslations();

    const dashboardCardsContent: DashboardCardContent[] = [
        {
            title: t(keys.quick_answer_card_title),
            description: t(keys.quick_answer_card_description),
            action: cardAction.quickAnswer,
        },
        {
            title: t(keys.template_card_title),
            description: t(keys.all_templates_card_description),
            action: cardAction.allTemplates,
        },
        {
            title: t(keys.upload_file_card_title),
            description: t(keys.upload_file_card_description),
            action: cardAction.uploadFile,
        },
    ];

    return (
        <FlexCol gap="4">
            <Text variant="header3">{t(keys.recommended_actions_title)}</Text>
            <FlexRow gap="4" className="h-[190px]">
                {dashboardCardsContent.map((card) => (
                    <Card key={card.action} w="1/3">
                        <FlexCol gap="2" h="full">
                            <Card.Title>{card.title}</Card.Title>
                            <Card.Description>
                                {card.description}
                            </Card.Description>
                        </FlexCol>
                        <Card.CtaSection>
                            <QuestionnaireCardAction action={card.action} />
                        </Card.CtaSection>
                    </Card>
                ))}
            </FlexRow>
        </FlexCol>
    );
};

const QuestionnaireCardAction: FC<{
    action: CardAction;
}> = ({ action }) => {
    return action === cardAction.quickAnswer ? (
        <QuickAnswerModal />
    ) : action === cardAction.uploadFile ? (
        <CreateQuestionnaireFromUploadModal />
    ) : (
        action === cardAction.allTemplates && (
            <CreateQuestionnaireModal isTemplate />
        )
    );
};

type DashboardCardContent = {
    title: string;
    description: string;
    action: CardAction;
    template?: QuestionnaireTemplate;
};

const cardAction = {
    quickAnswer: "quickAnswer",
    uploadFile: "uploadFile",
    allTemplates: "allTemplates",
} as const;

type CardAction = keyof typeof cardAction;
